import React, { useState, useEffect, useContext } from 'react';
import API from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiUserPlus } from 'react-icons/fi';

const ManageEditors = () => {
  const { role } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [editors, setEditors] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // If not chairman, redirect back to dashboard
    if (role && role !== 'chairman') {
      navigate('/admin/dashboard');
      return;
    }

    if (role === 'chairman') {
      fetchEditors();
    }
  }, [role, navigate]);

  const fetchEditors = async () => {
    try {
      setLoading(true);
      const res = await API.get('/admin/editors');
      setEditors(res.data);
    } catch (error) {
      console.error('Error fetching editors:', error);
      alert('Failed to load editors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEditor = async (e) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;

    try {
      setCreating(true);
      await API.post('/admin/editors', { username: newUsername, password: newPassword });
      alert('Editor created successfully');
      setNewUsername('');
      setNewPassword('');
      fetchEditors(); // reload list
    } catch (error) {
      console.error('Error creating editor:', error);
      if (error.response && error.response.status === 409) {
        alert('Username already exists');
      } else {
        alert('Failed to create editor');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEditor = async (id) => {
    if (window.confirm('Are you sure you want to delete this editor? They will lose access immediately.')) {
      try {
        await API.delete(`/admin/editors/${id}`);
        alert('Editor deleted successfully');
        fetchEditors();
      } catch (error) {
        console.error('Error deleting editor:', error);
        alert('Failed to delete editor');
      }
    }
  };

  if (role !== 'chairman') {
    return null; // Don't render anything while redirecting
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="mb-8 bg-slate-900 text-white p-8 rounded-2xl shadow-lg border border-slate-800 relative overflow-hidden">
         <div className="absolute right-0 top-0 w-64 h-64 bg-red-600 rounded-full opacity-10 -translate-y-1/2 translate-x-1/3"></div>
         <h1 className="text-3xl font-black tracking-tight mb-2 relative z-10">Chairman Desk</h1>
         <p className="text-slate-400 relative z-10">Manage editor accounts for the Pratyaksha News platform.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create Editor Form */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-6 border-b pb-3 flex items-center">
              <FiUserPlus className="mr-2 text-red-600" />
              Add New Editor
            </h2>
            
            <form onSubmit={handleCreateEditor} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  required
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="e.g. editor1"
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a secure password"
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                />
              </div>
              
              <button
                type="submit"
                disabled={creating}
                className={`w-full text-white font-bold py-3 rounded mt-4 transition ${creating ? 'bg-gray-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-black'}`}
              >
                {creating ? 'Creating...' : 'Create Editor Account'}
              </button>
            </form>
          </div>
        </div>

        {/* Existing Editors List */}
        <div className="lg:col-span-2">
           <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-6 border-b pb-3">Active Editors</h2>
            
            {loading ? (
              <div className="flex justify-center items-center h-32">
                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : editors.length === 0 ? (
              <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                No editors have been created yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-sm">
                      <th className="p-4 font-semibold text-gray-600">Username</th>
                      <th className="p-4 font-semibold text-gray-600">Created At</th>
                      <th className="p-4 font-semibold text-gray-600 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editors.map((editor) => (
                      <tr key={editor.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="p-4 font-medium text-gray-800">
                          {editor.username}
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {new Date(editor.created_at).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => handleDeleteEditor(editor.id)}
                            className="text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded transition"
                            title="Delete Editor"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageEditors;
